// LocationInspectionFormPdfTemplate.js
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import { styles as base } from "./styles/PdfStyles";
import hardcodedLogo from "../../assets/img/hardcoded-logos/mc-business.jpg";
import {
  SECTIONS,
  ANEXA_GROUPS,
  CONCLUSION_KEYS,
  EXTRA_OBSERVATION_KEYS,
  GRADE_LEGEND,
  MAX_SCORE,
  anexaExistaKey,
  anexaObsKey,
  computeTotals,
} from "../../views/inspections/inspectionForm.schema";

const safe = (v) => (v == null ? "" : String(v));

const BORDER = "#111827";

const colors = {
  headerBg: "#e5e7eb",
  sectionBg: "#f3f4f6",
};

function Cell({ children, width, align = "left", bold, bg, last }) {
  return (
    <View
      style={{
        width,
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderRightWidth: last ? 1 : 0,
        borderColor: BORDER,
        backgroundColor: bg || undefined,
        paddingVertical: 3,
        paddingHorizontal: 4,
        justifyContent: "center",
      }}
    >
      <Text style={{ fontSize: 7.5, textAlign: align, fontWeight: bold ? "bold" : "normal" }}>{children}</Text>
    </View>
  );
}

function Row({ children }) {
  return <View style={{ flexDirection: "row" }}>{children}</View>;
}

function Line({ label, value }) {
  return (
    <Text style={{ fontSize: 9, marginBottom: 4 }}>
      <Text style={{ fontWeight: "bold" }}>{label} </Text>
      <Text style={{ textDecoration: "underline", textDecorationStyle: "dotted" }}>
        {safe(value).trim() || "________________________"}
      </Text>
    </Text>
  );
}

// Page 1 column widths (total 100%)
const W_ASPECT = "76%";
const W_PONDERE = "12%";
const W_PUNCTE = "12%";

// Page 2 column widths
const W_ACT = "62%";
const W_EXISTA = "18%";
const W_TERMEN = "20%";

export default function LocationInspectionFormPdfTemplate({ data }) {
  const fields = data?.fields || {};
  const totals = computeTotals(fields);

  const magazin = safe(data?.locationName);
  const inspectionDate = safe(data?.inspectionDateLabel);
  const inspectorName = safe(data?.inspectorName);
  const inspectorFunctie = safe(fields.verificatorFunctie);
  const operatorName = safe(data?.operatorName);

  const verificatorLine = [inspectorName, inspectorFunctie].filter(Boolean).join(" - ");

  return (
    <Document>
      {/* ============================ PAGE 1 - FISA ============================ */}
      <Page size="A4" style={{ ...base.page, padding: 28 }}>
        <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 8 }}>
          <Image src={hardcodedLogo} style={{ width: 110, height: 55, objectFit: "contain" }} />
          <View style={{ flex: 1, alignItems: "center" }}>
            <Text style={{ fontSize: 9, fontWeight: "bold", textAlign: "center" }}>
              MONITORIZAREA MAGAZINELOR DE CATRE MANAGERUL
            </Text>
            <Text style={{ fontSize: 9, fontWeight: "bold", textAlign: "center" }}>
              ZONAL DE VANZARI SI DIRECTORUL GENERAL
            </Text>
          </View>
        </View>

        <View style={{ alignItems: "center", marginBottom: 10 }}>
          <Text style={{ fontSize: 14, fontWeight: "bold" }}>FISA DE VERIFICARE MAGAZIN</Text>
        </View>

        <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 8 }}>
          <View style={{ width: "60%" }}>
            <Line label="Magazin:" value={magazin} />
          </View>
          <View style={{ width: "38%" }}>
            <Line label="Data verificarii:" value={inspectionDate} />
          </View>
        </View>

        {/* Scoring table */}
        <View style={{ borderTopWidth: 1, borderColor: BORDER }}>
          <Row>
            <Cell width={W_ASPECT} bold bg={colors.headerBg}>
              ASPECT VERIFICAT
            </Cell>
            <Cell width={W_PONDERE} bold align="center" bg={colors.headerBg}>
              Pondere
            </Cell>
            <Cell width={W_PUNCTE} bold align="center" bg={colors.headerBg} last>
              Puncte obtinute
            </Cell>
          </Row>

          {SECTIONS.map((section) => (
            <View key={section.key}>
              <Row>
                <Cell width={W_ASPECT} bold bg={colors.sectionBg}>
                  {`${section.no}. ${section.title}`}
                </Cell>
                <Cell width={W_PONDERE} bold align="center" bg={colors.sectionBg}>
                  {`${section.max} (total)`}
                </Cell>
                <Cell width={W_PUNCTE} bold align="center" bg={colors.sectionBg} last>
                  {String(totals.sectionTotals[section.key] ?? 0)}
                </Cell>
              </Row>

              {section.items.map((item) => (
                <Row key={item.key}>
                  <Cell width={W_ASPECT}>{`${item.no}  ${item.label}`}</Cell>
                  <Cell width={W_PONDERE} align="center">
                    {String(item.weight)}
                  </Cell>
                  <Cell width={W_PUNCTE} align="center" last>
                    {safe(fields[item.key])}
                  </Cell>
                </Row>
              ))}

              <Row>
                <Cell width="100%" last>
                  {`Observatii: ${safe(fields[section.observationsKey])}`}
                </Cell>
              </Row>
            </View>
          ))}

          <Row>
            <Cell width={W_ASPECT} bold bg={colors.headerBg}>
              SCOR FINAL
            </Cell>
            <Cell width={W_PONDERE} bold align="center" bg={colors.headerBg}>
              {String(MAX_SCORE)}
            </Cell>
            <Cell width={W_PUNCTE} bold align="center" bg={colors.headerBg} last>
              {String(totals.total)}
            </Cell>
          </Row>
        </View>

        {/* Nota + grade legend */}
        <View style={{ flexDirection: "row", marginTop: 10 }}>
          <View style={{ width: "58%" }}>
            <Text style={{ fontSize: 8, fontWeight: "bold" }}>Nota:</Text>
            <Text style={{ fontSize: 8 }}>{`*Nota maxima este de ${MAX_SCORE} de puncte.`}</Text>
            <Text style={{ fontSize: 14, fontWeight: "bold", marginTop: 6 }}>{totals.grade}</Text>
          </View>

          <View style={{ width: "42%" }}>
            <View style={{ borderTopWidth: 1, borderColor: BORDER }}>
              <Row>
                <Cell width="50%" bold align="center" bg={colors.headerBg}>
                  Obtinut
                </Cell>
                <Cell width="50%" bold align="center" bg={colors.headerBg} last>
                  Procent obtinut din total%
                </Cell>
              </Row>
              <Row>
                <Cell width="50%" align="center">
                  {String(totals.total)}
                </Cell>
                <Cell width="50%" align="center" last>
                  {totals.percentLabel}
                </Cell>
              </Row>
            </View>

            <Text style={{ fontSize: 8, fontWeight: "bold", marginTop: 6, marginBottom: 2 }}>
              Gradul de conformare
            </Text>
            <View style={{ borderTopWidth: 1, borderColor: BORDER }}>
              {GRADE_LEGEND.map((g) => (
                <Row key={g.label}>
                  <Cell width="55%">{g.label}</Cell>
                  <Cell width="45%" align="center" last>
                    {g.range}
                  </Cell>
                </Row>
              ))}
            </View>
          </View>
        </View>

        {/* Conclusions */}
        <View style={{ marginTop: 12 }}>
          <Text style={{ fontSize: 9, fontWeight: "bold", marginBottom: 4 }}>
            Constatari si concluzii, recomandari:
          </Text>
          <View style={{ borderTopWidth: 1, borderColor: BORDER }}>
            {CONCLUSION_KEYS.map((key, idx) => (
              <Row key={key}>
                <Cell width="6%" align="center">
                  {String(idx + 1)}
                </Cell>
                <Cell width="94%" last>
                  {safe(fields[key])}
                </Cell>
              </Row>
            ))}
          </View>
        </View>

        {/* Names - no signature boxes */}
        <View style={{ flexDirection: "row", justifyContent: "space-between", marginTop: 18 }}>
          <View style={{ width: "48%" }}>
            <Text style={{ fontSize: 8, marginBottom: 3 }}>Verificatorul magazinului, functia, numele</Text>
            <Text style={{ fontSize: 10, fontWeight: "bold" }}>{verificatorLine || "-"}</Text>
          </View>
          <View style={{ width: "48%" }}>
            <Text style={{ fontSize: 8, marginBottom: 3 }}>Operator magazin, Nume si Prenume</Text>
            <Text style={{ fontSize: 10, fontWeight: "bold" }}>{operatorName || "-"}</Text>
          </View>
        </View>
      </Page>

      {/* ============================ PAGE 2 - ANEXA ============================ */}
      <Page size="A4" style={{ ...base.page, padding: 28 }}>
        <View style={{ alignItems: "center", marginBottom: 10 }}>
          <Text style={{ fontSize: 13, fontWeight: "bold" }}>ANEXA LA FISA DE VERIFICARE MAGAZIN</Text>
        </View>

        <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 8 }}>
          <View style={{ width: "60%" }}>
            <Line label="Magazin:" value={magazin} />
          </View>
          <View style={{ width: "38%" }}>
            <Line label="Data verificarii:" value={inspectionDate} />
          </View>
        </View>

        <View style={{ borderTopWidth: 1, borderColor: BORDER }}>
          {ANEXA_GROUPS.map((group) => (
            <View key={group.key}>
              <Row>
                <Cell width={W_ACT} bold bg={colors.headerBg}>
                  ACTE OBLIGATORII PE MAGAZIN
                </Cell>
                <Cell width={W_EXISTA} bold align="center" bg={colors.headerBg}>
                  EXISTA/ NU EXISTA
                </Cell>
                <Cell width={W_TERMEN} bold align="center" bg={colors.headerBg} last>
                  {group.secondColumn}
                </Cell>
              </Row>

              <Row>
                <Cell width="100%" bold bg={colors.sectionBg} last>
                  {group.title}
                </Cell>
              </Row>

              {group.rows.map((row) => (
                <Row key={row.key}>
                  <Cell width={W_ACT}>{row.label}</Cell>
                  <Cell width={W_EXISTA} align="center">
                    {fields[anexaExistaKey(row.key)] === "DA"
                      ? "EXISTA"
                      : fields[anexaExistaKey(row.key)] === "NU"
                      ? "NU EXISTA"
                      : ""}
                  </Cell>
                  <Cell width={W_TERMEN} align="center" last>
                    {safe(fields[anexaObsKey(row.key)])}
                  </Cell>
                </Row>
              ))}
            </View>
          ))}

          <Row>
            <Cell width={W_ACT} bold bg={colors.sectionBg}>
              Observatii suplimentare
            </Cell>
            <Cell width={W_EXISTA}>{safe(fields[EXTRA_OBSERVATION_KEYS[0]])}</Cell>
            <Cell width={W_TERMEN} last>
              {safe(fields[EXTRA_OBSERVATION_KEYS[1]])}
            </Cell>
          </Row>
        </View>
      </Page>
    </Document>
  );
}
